import axios from 'axios';
import storage from './storage';
import { Platform } from 'react-native';

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // For web platform, check if running on localhost
  if (Platform.OS === 'web') {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5145';
    }
    // Production web (Vercel)
    return 'https://kalyanamitra.in';
  }
  
  // For native platforms (iOS/Android), use localhost for development
  if (__DEV__) {
    // Use your computer's IP address for testing on physical devices
    // For Android emulator: 10.0.2.2
    // For iOS simulator: localhost works
    // For physical devices: use your machine's IP (e.g., 'http://192.168.1.100:5145')
    return Platform.OS === 'android' 
      ? 'http://10.0.2.2:5145'  // Android emulator
      : 'http://localhost:5145';  // iOS simulator
  }
  
  // Production for native apps
  return 'https://kalyanamitra.in';
};

const API_BASE_URL = getApiBaseUrl();

// Log the API URL for debugging
console.log('API Base URL:', API_BASE_URL);

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await storage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error retrieving auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      if (status === 401) {
        // Unauthorized - clear token and redirect to login
        await storage.deleteItem('authToken');
        await storage.deleteItem('userData');
      }
      
      // Extract error message from response
      const errorMessage = data?.message || data?.title || error.message;
      return Promise.reject(new Error(errorMessage));
    } else if (error.request) {
      // Request made but no response received
      return Promise.reject(new Error('Network error. Please check your internet connection.'));
    } else {
      // Something else happened
      return Promise.reject(new Error(error.message || 'An unexpected error occurred.'));
    }
  }
);

// S3Photo API methods - 3-step upload process
// Step 1: Get presigned URL for S3 upload
export const getUploadUrl = async ({ memberId, fileName, contentType }) => {
  return apiClient.post('/api/S3Photo/upload-url', {
    memberId,
    fileName,
    contentType
  });
};

// Step 2: Upload file directly to S3 using presigned URL
export const uploadToS3 = async (presignedUrl, file, contentType, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentComplete = (e.loaded / e.total) * 100;
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve({ status: xhr.status });
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    
    // Handle different file types
    let bodyData = file;
    if (file && file.isNative && file.data instanceof Uint8Array) {
      // For native platforms - use Uint8Array directly
      bodyData = file.data;
    } else if (file instanceof Blob) {
      // For web - Blob is fine as is
      bodyData = file;
    } else if (file instanceof Uint8Array || file instanceof ArrayBuffer) {
      // Direct binary data
      bodyData = file;
    }
    
    xhr.send(bodyData);
  });
};

// Step 3: Save photo metadata after successful S3 upload
export const savePhotoMetadata = async ({
  memberId,
  photoKey,
  s3Url,
  fileName,
  contentType,
  fileSizeBytes,
  isProfilePhoto = false,
  photoIndex = 0
}) => {
  return apiClient.post('/api/S3Photo/save', {
    memberId,
    photoKey,
    s3Url,
    fileName,
    contentType,
    fileSizeBytes,
    isProfilePhoto,
    photoIndex
  });
};

// Note: Get member photos - endpoint doesn't exist, photos come from shortProfile
// export const getMemberPhotos = async (memberId, includeInactive = false) => {
//   console.log('Getting member photos:', { memberId, includeInactive });
//   const params = includeInactive ? '?includeInactive=true' : '';
//   const response = await apiClient.get(`/api/members/${memberId}/photos${params}`);
//   return response.data;
// };

// Set primary photo
export const setPrimaryPhoto = async (memberId, photoId) => {
  console.log('Setting primary photo:', { memberId, photoId });
  const response = await apiClient.put(`/api/members/${memberId}/photos/${photoId}/set-primary`);
  return response.data;
};

// Delete photo - using actual backend endpoint
export const deleteProfilePhoto = async ({ memberId, photoId, photoKey }) => {
  console.log('API deleteProfilePhoto called with:', { memberId, photoId, photoKey });
  try {
    // Use S3Photo endpoint with DOUBLE-encoded key (this is the only method that works)
    if (photoKey) {
      // DOUBLE ENCODE: This matches the working website behavior
      // Example: "photos/file name.jpeg" -> "photos%2Ffile%20name.jpeg" -> "photos%252Ffile%2520name.jpeg"
      const encodedPhotoKey = encodeURIComponent(encodeURIComponent(photoKey));
      const deleteUrl = `/api/S3Photo/${encodedPhotoKey}`;
      console.log('DELETE URL:', deleteUrl);
      console.log('Original photoKey:', photoKey);
      console.log('Double-encoded photoKey:', encodedPhotoKey);
      
      const response = await apiClient.delete(deleteUrl);
      console.log('Delete API response:', response.status, response.data);
      return response;
    }
    
    throw new Error('Photo key is required to delete photo');
  } catch (error) {
    console.error('Delete API error:', error.response?.data || error.message);
    throw error;
  }
};

export default apiClient;
export { API_BASE_URL };
